/**
 * CompBrowser — client-side filtering and sorting for the Comparable
 * Contracts browser. The server renders every candidate row once (with a
 * compact data-f JSON payload per row); this hook owns all filter state so
 * chips, sliders, and search feel instant with zero server round-trips.
 *
 * Server-seeded defaults (the "educated lens") arrive via data-defaults:
 *   { positions: [...], min_salary, max_salary, archetype, min_games }
 * Every default maps to a visible control the user can uncheck — the lens
 * narrows the view by default but never limits what can be seen.
 */

import {safeStorage} from "../safe-storage"

const store = safeStorage("session")

const STRING_COLS = new Set(["n", "t", "p"])
const ASC_DEFAULT = new Set(["n", "t", "a"])

function fmtSalary(v) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v > 0) return `$${Math.round(v / 1_000)}k`
  return "$0"
}

const COLLAPSE_KEY = "compBrowserFiltersCollapsed"

export default {
  mounted() {
    this.defaults = JSON.parse(this.el.dataset.defaults || "{}")
    this.ceiling = parseInt(this.el.dataset.salaryCeiling || "70000000", 10)
    this.state = this.defaultState()
    this.collapsed = store.getItem(COLLAPSE_KEY) === "1"

    // Click delegation: chips, sort headers, view toggle, reset/clear.
    this.el.addEventListener("click", (e) => {
      if (e.target.closest('[data-role="filter-toggle"]')) {
        this.collapsed = !this.collapsed
        store.setItem(COLLAPSE_KEY, this.collapsed ? "1" : "0")
        return this.apply()
      }
      const pos = e.target.closest("[data-pos]")
      if (pos) return this.togglePosition(pos.dataset.pos)

      const arch = e.target.closest("[data-arch]")
      if (arch) return this.toggleArchetype(arch.dataset.arch)

      const status = e.target.closest("[data-status]")
      if (status) return this.toggleStatus(status.dataset.status)

      if (e.target.closest('[data-role="reset"]')) {
        this.state = this.defaultState()
        return this.apply()
      }

      if (e.target.closest('[data-role="clear"]') || e.target.closest('[data-role="empty-clear"]')) {
        this.state = this.clearedState()
        return this.apply()
      }

      if (e.target.closest('[data-role="view-toggle"]')) {
        this.state.view = this.state.view === "stats" ? "advanced" : "stats"
        return this.apply()
      }

      const th = e.target.closest("[data-sort-col]")
      if (th) {
        e.preventDefault()
        e.stopPropagation()
        return this.toggleSort(th.dataset.sortCol, e.shiftKey)
      }
    })

    // Input delegation: search, age bounds, salary sliders.
    this.el.addEventListener("input", (e) => {
      const role = e.target.dataset.role
      if (!role) return

      if (role === "search") this.state.search = e.target.value.trim().toLowerCase()
      if (role === "min-age") this.state.minAge = e.target.value ? parseInt(e.target.value, 10) : null
      if (role === "max-age") this.state.maxAge = e.target.value ? parseInt(e.target.value, 10) : null

      if (role === "sal-min") {
        this.state.salMin = Math.min(parseInt(e.target.value, 10), this.state.salMax)
      }
      if (role === "sal-max") {
        this.state.salMax = Math.max(parseInt(e.target.value, 10), this.state.salMin)
      }

      this.apply()
    })

    this.apply()
  },

  // LiveView patches (comparison open/close, scenario changes) reset DOM
  // order, visibility, and input values to the server-rendered baseline —
  // re-apply our state on every update.
  updated() {
    this.apply()
  },

  defaultState() {
    const d = this.defaults
    return {
      search: "",
      positions: new Set(d.positions || []),
      archetypes: new Set(d.archetype ? [d.archetype] : []),
      status: new Set(d.status || []),
      salMin: d.min_salary || 0,
      salMax: d.max_salary != null ? Math.min(d.max_salary, this.ceiling) : this.ceiling,
      minAge: null,
      maxAge: null,
      sort: [["s", "desc"]],
      view: "stats",
    }
  },

  clearedState() {
    return {
      ...this.defaultState(),
      positions: new Set(),
      archetypes: new Set(),
      status: new Set(),
      salMax: this.ceiling,
    }
  },

  togglePosition(pos) {
    this.state.positions.has(pos) ? this.state.positions.delete(pos) : this.state.positions.add(pos)
    this.apply()
  },

  toggleArchetype(key) {
    this.state.archetypes.has(key) ? this.state.archetypes.delete(key) : this.state.archetypes.add(key)
    this.apply()
  },

  // "fa" | "signed" — selecting neither or both means no status filter.
  toggleStatus(key) {
    this.state.status.has(key) ? this.state.status.delete(key) : this.state.status.add(key)
    this.apply()
  },

  toggleSort(col, shift) {
    const dir = ASC_DEFAULT.has(col) ? "asc" : "desc"
    const idx = this.state.sort.findIndex(([c]) => c === col)

    if (idx === -1) {
      const entry = [col, dir]
      this.state.sort = shift ? [...this.state.sort, entry] : [entry]
    } else {
      const [, current] = this.state.sort[idx]
      if (current === dir) {
        this.state.sort[idx] = [col, dir === "asc" ? "desc" : "asc"]
      } else {
        this.state.sort.splice(idx, 1)
        if (this.state.sort.length === 0) this.state.sort = [["s", "desc"]]
      }
    }
    this.apply()
  },

  apply() {
    this.syncControls()

    const tbody = this.el.querySelector('[data-role="rows"]')
    if (!tbody) return // comparison view is open; nothing to filter

    const rows = Array.from(tbody.querySelectorAll("tr[data-f]")).map((el) => ({
      el,
      f: JSON.parse(el.dataset.f),
    }))

    const s = this.state
    let visible = 0

    for (const row of rows) {
      const f = row.f
      const show =
        (!s.search || f.n.includes(s.search)) &&
        (s.positions.size === 0 || s.positions.has(f.p)) &&
        (s.archetypes.size === 0 || (f.ar || []).some((a) => s.archetypes.has(a))) &&
        (s.status.size === 0 || s.status.has(f.fa ? "fa" : "signed")) &&
        f.s >= s.salMin &&
        f.s <= s.salMax &&
        (s.minAge == null || (f.a != null && f.a >= s.minAge)) &&
        (s.maxAge == null || (f.a != null && f.a <= s.maxAge))

      row.el.classList.toggle("hidden", !show)
      if (show) visible++
    }

    // Sort all rows (hidden included — their order is irrelevant) and
    // re-append in order.
    rows.sort((a, b) => this.compare(a.f, b.f))
    for (const row of rows) tbody.appendChild(row.el)

    const count = this.el.querySelector('[data-role="count"]')
    if (count) count.textContent = `Showing ${visible} of ${rows.length} players`

    const empty = this.el.querySelector('[data-role="empty"]')
    if (empty) empty.classList.toggle("hidden", visible > 0)

    const table = this.el.querySelector('[data-role="comp-table"]')
    if (table) table.dataset.view = this.state.view
  },

  compare(a, b) {
    for (const [col, dir] of this.state.sort) {
      let va = a[col]
      let vb = b[col]
      if (va == null && vb == null) continue
      if (va == null) return 1 // nulls last regardless of direction
      if (vb == null) return -1
      if (va === vb) continue

      let cmp
      if (STRING_COLS.has(col)) {
        cmp = String(va).localeCompare(String(vb))
      } else {
        cmp = va < vb ? -1 : 1
      }
      return dir === "asc" ? cmp : -cmp
    }
    return 0
  },

  // How many filter groups are narrowing the list right now.
  activeFilterCount() {
    const s = this.state
    let n = 0
    if (s.search) n++
    if (s.positions.size > 0) n++
    if (s.archetypes.size > 0) n++
    if (s.status.size > 0) n++
    if (s.minAge != null || s.maxAge != null) n++
    if (s.salMin > 0 || s.salMax < this.ceiling) n++
    return n
  },

  syncControls() {
    const s = this.state
    const active = "btn-primary"

    const body = this.el.querySelector('[data-role="filter-body"]')
    if (body) body.classList.toggle("hidden", this.collapsed)

    const chevron = this.el.querySelector('[data-role="filter-chevron"]')
    if (chevron) chevron.style.transform = this.collapsed ? "rotate(-90deg)" : ""

    const summary = this.el.querySelector('[data-role="filter-summary"]')
    if (summary) {
      const n = this.activeFilterCount()
      summary.textContent = n > 0 ? `· ${n} active` : ""
    }

    this.el.querySelectorAll("[data-pos]").forEach((el) => {
      el.classList.toggle(active, s.positions.has(el.dataset.pos))
    })
    this.el.querySelectorAll("[data-arch]").forEach((el) => {
      el.classList.toggle(active, s.archetypes.has(el.dataset.arch))
    })
    this.el.querySelectorAll("[data-status]").forEach((el) => {
      el.classList.toggle(active, s.status.has(el.dataset.status))
    })

    const search = this.el.querySelector('[data-role="search"]')
    if (search && search !== document.activeElement) {
      // restore after LiveView patches reset the value
      search.value = s.search
    }

    const minAge = this.el.querySelector('[data-role="min-age"]')
    if (minAge && minAge !== document.activeElement) minAge.value = s.minAge ?? ""
    const maxAge = this.el.querySelector('[data-role="max-age"]')
    if (maxAge && maxAge !== document.activeElement) maxAge.value = s.maxAge ?? ""

    const salMin = this.el.querySelector('[data-role="sal-min"]')
    const salMax = this.el.querySelector('[data-role="sal-max"]')
    if (salMin) salMin.value = s.salMin
    if (salMax) salMax.value = s.salMax

    const fill = this.el.querySelector('[data-role="sal-fill"]')
    if (fill && this.ceiling > 0) {
      fill.style.left = `${(s.salMin / this.ceiling) * 100}%`
      fill.style.right = `${100 - (s.salMax / this.ceiling) * 100}%`
    }

    const label = this.el.querySelector('[data-role="sal-label"]')
    if (label) {
      const top = s.salMax >= this.ceiling ? "max" : fmtSalary(s.salMax)
      label.textContent = `${fmtSalary(s.salMin)} — ${top}`
    }

    const viewLabel = this.el.querySelector('[data-role="view-label"]')
    if (viewLabel) viewLabel.textContent = s.view === "stats" ? "Advanced" : "Stats"
  },
}
